import type { IConversation, IPaginatedResponse } from '@/api';
import { DEFAULT_PAGE_SIZE } from '@/constants/common';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { formatDateTime } from '@/utils/formatDateTime';
import { isValidImageUrl } from '@/utils/isValidImageUrl';
import type {
	InfiniteData,
	UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

// Type for the API hook function
type ApiHook = (options: { size: number }) => UseInfiniteQueryResult<
	InfiniteData<IPaginatedResponse<IConversation>>,
	Error
>;

export const useForYouList = (useApiHook: ApiHook) => {
	// Data fetching and transformation
	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useApiHook({ size: DEFAULT_PAGE_SIZE });

	const items = useMemo(
		() =>
			data?.pages?.flatMap((page) =>
				page.data.map((item) => ({
					id: item.id,
					title: item.name,
					reacted: item.reacted,
					content: item.content,
					userAvatar: item.userAvatar,
					userFullName: item.userFullName,
					reactionCount: item.reactionCount,
					commentCount: item.commentCount,
					viewCount: item.viewCount,
					timeAgo: formatDateTime(item.createdAt),
					images: item.images.filter(isValidImageUrl),
					userId: item.userId,
				})),
			) ?? [],
		[data],
	);

	const memoizedFetchNextPage = useCallback(fetchNextPage, []);

	// Infinite scroll handling
	const {
		loadMoreRef,
		isLoading: isInfiniteLoading,
		hasMore,
	} = useInfiniteScroll({
		hasNextPage,
		isLoading,
		isFetchingNextPage,
		fetchNextPage: memoizedFetchNextPage,
	});

	return {
		items,
		isLoading: isInfiniteLoading,
		hasMore,
		loadMoreRef,
		data,
	};
};
